import { TrashIcon } from '@heroicons/react/24/outline';
import { AutomationRule, Rule } from '../partnerCreateBlacklistRule/types.ts';
import { useEffect, useRef, useState } from 'react';
import Popup from '../../../../../components/general/Popup.tsx';
import type { RuleOperatorEnum } from '../../../../../generated/enums.ts';

const OPERATOR_LABELS: Record<RuleOperatorEnum, string> = {
    EQUALS: 'is gelijk aan',
    NOT_EQUALS: 'is niet gelijk aan',
    CONTAINS: 'bevat',
    LESS_THAN: 'is kleiner dan',
    GREATER_THAN: 'is groter dan',
    BETWEEN: 'ligt tussen',
};

const FIELD_LABELS: Record<string, string> = {
    business_id: 'klant',
    relation_name: 'relatie',
    relation_iban: 'IBAN',
    booking_date: 'datum',
    amount: 'bedrag',
    desc: 'omschrijving',
};

function formatMoneyAmount(value: number): string {
    return Number.isFinite(value)
        ? new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(value)
        : String(value ?? '');
}

function formatDate(value: string | Date | null): string {
    if (!value) return '';
    const d = value instanceof Date ? value : new Date(value);
    return !Number.isNaN(d.getTime())
        ? d.toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' })
        : String(value ?? '');
}

function formatRuleValue(field: string, condition: Rule, businessName?: string): string {
    const v = condition?.operand;

    if (field === 'amount') {
        const n = typeof v === 'number' ? v : Number(v);
        const ceiling = condition?.context?.ceiling;
        const upper = ceiling != null ? (typeof ceiling === 'number' ? ceiling : Number(ceiling)) : null;

        if (condition?.operator === 'BETWEEN' && upper != null) {
            return `${formatMoneyAmount(n)} en ${formatMoneyAmount(upper)}`;
        }
        return formatMoneyAmount(n);
    }

    if (field === 'booking_date') {
        const d1 = v ? new Date(v as string) : null;
        const d2 = condition?.context?.ceiling ? new Date(condition.context.ceiling as string) : null;

        if (condition?.operator === 'BETWEEN' && d2) {
            return `${formatDate(d1)} en ${formatDate(d2)}`;
        }
        return formatDate(d1);
    }

    if (field === 'business_id') {
        if (!v) return 'alle klanten';
        return `klant ${businessName || v}`;
    }

    return typeof v === 'string' ? `"${v}"` : String(v ?? '');
}

function generateAutomationRuleDescription(rule: AutomationRule): string {
    const rawRules = rule?.rule?.rules ?? [];
    const connective = rule?.rule?.connective === 'OR' ? 'of' : 'en';

    if (!Array.isArray(rawRules) || rawRules.length === 0) {
        return `Regel ${rule.rule_id}`;
    }

    const parts = rawRules
        .filter((r): r is Rule => r?.value_field_name != null && r?.operator != null)
        .map(r => {
            const field = String(r.value_field_name);
            const label = FIELD_LABELS[field] ?? field;
            const op = OPERATOR_LABELS[r.operator] ?? String(r.operator);
            const value = formatRuleValue(field, r, rule.business_name);

            if (field === 'business_id') return value;
            return `${label} ${op} ${value}`;
        });

    if (parts.length === 0) return `Regel ${rule.rule_id}`;

    const description = parts.join(` ${connective} `);
    return description.charAt(0).toUpperCase() + description.slice(1);
}

interface RulesTabProps {
    rules: AutomationRule[];
    onDeleteRule: (id: string) => void;
    highlightedRuleId?: string;
}

export function RulesTab({ rules, onDeleteRule, highlightedRuleId }: RulesTabProps) {
    const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState<string | null>(null);
    const ruleElementsRef = useRef(new Map<string, HTMLDivElement>());
    const scrolledRuleIdRef = useRef<string | null>(null);

    useEffect(() => {
        if (!highlightedRuleId || scrolledRuleIdRef.current === highlightedRuleId) return;
        const element = ruleElementsRef.current.get(highlightedRuleId);
        if (!element) return;
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        scrolledRuleIdRef.current = highlightedRuleId;
    }, [highlightedRuleId, rules]);

    const handleDeleteConfirm = () => {
        if (deleteConfirmationOpen) {
            onDeleteRule(deleteConfirmationOpen);
            setDeleteConfirmationOpen(null);
        }
    };

    return (
        <div>
            <h3 className="text-xl text-gray-900 font-semibold py-3 pt-6">Jouw automatiseringen</h3>
            <div className="space-y-4 mt-4">
                {rules.map(rule => {
                    const isHighlighted = rule.rule_id === highlightedRuleId;
                    return (
                        <div
                            key={rule.rule_id}
                            ref={(element) => {
                                if (element) {
                                    ruleElementsRef.current.set(rule.rule_id, element);
                                } else {
                                    ruleElementsRef.current.delete(rule.rule_id);
                                }
                            }}
                            aria-current={isHighlighted ? 'true' : undefined}
                            className={`border rounded-lg p-4 flex justify-between items-center bg-white shadow-sm${isHighlighted ? ' ring-2 ring-primary border-primary' : ''}`}
                        >
                            <p className="text-sm text-gray-800 mr-4">
                                {generateAutomationRuleDescription(rule)}
                            </p>
                            <button
                                onClick={() => setDeleteConfirmationOpen(rule.rule_id)}
                                className="text-red-500 hover:text-red-700 p-2 rounded-full transition-colors duration-200"
                                aria-label="Verwijder regel"
                                title="Verwijder regel"
                            >
                                <TrashIcon className="h-5 w-5" />
                            </button>
                        </div>
                    );
                })}
                {rules.length === 0 && (
                    <p className="text-sm text-gray-600 italic">Geen regels geconfigureerd</p>
                )}
            </div>
            <Popup
                open={deleteConfirmationOpen}
                onClose={() => setDeleteConfirmationOpen(null)}
                onConfirm={handleDeleteConfirm}
                title="Regel verwijderen"
                variant="danger"
                confirmButtonDisplayLabel="Verwijderen"
            >
                <p className="text-sm text-gray-700">
                    Weet je zeker dat je deze regel wilt verwijderen?
                </p>
            </Popup>
        </div>
    );
}